/**
 * Shared pinning state for Analytics Overview and Catalog.
 * Persists showPinnedOnly and pinnedIds in localStorage.
 */

import { useState, useCallback } from 'react';

const PINNED_VIEW_KEY = 'nb:analytics:overview:showPinnedOnly';
const PINNED_IDS_KEY = 'nb:analytics:overview:pinnedIds';

function loadPinnedPref(): boolean {
  try {
    const v = localStorage.getItem(PINNED_VIEW_KEY);
    if (v === 'false') return false;
    return true;
  } catch {
    return true;
  }
}

function savePinnedPref(pinned: boolean): void {
  try {
    localStorage.setItem(PINNED_VIEW_KEY, String(pinned));
  } catch {
    /* noop */
  }
}

function loadPinnedIds(defaultIds: string[]): string[] {
  try {
    const raw = localStorage.getItem(PINNED_IDS_KEY);
    if (!raw) return defaultIds;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return defaultIds;
    const ids = parsed.filter((x): x is string => typeof x === 'string');
    return ids.length > 0 ? ids : defaultIds;
  } catch {
    return defaultIds;
  }
}

function savePinnedIds(ids: string[]): void {
  try {
    localStorage.setItem(PINNED_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* noop */
  }
}

export interface UseAnalyticsPinsOptions {
  /** Default pinned chart IDs when none stored (e.g. from CHART_REGISTRY.filter(c => c.pinned).map(c => c.id)) */
  defaultPinnedIds: string[];
  /** Initial showPinnedOnly when no stored value (default true so Overview shows pinned by default) */
  initialShowPinnedOnly?: boolean;
}

export interface UseAnalyticsPinsResult {
  showPinnedOnly: boolean;
  setShowPinnedOnly: (value: boolean) => void;
  pinnedIds: string[];
  setPinnedIds: React.Dispatch<React.SetStateAction<string[]>>;
  handlePinnedToggle: (pinned: boolean) => void;
  handleTogglePin: (chartId: string) => void;
}

export function useAnalyticsPins({
  defaultPinnedIds,
  initialShowPinnedOnly = true,
}: UseAnalyticsPinsOptions): UseAnalyticsPinsResult {
  const [showPinnedOnly, setShowPinnedOnlyState] = useState(() => {
    const stored = loadPinnedPref();
    return stored;
  });

  const [pinnedIds, setPinnedIds] = useState<string[]>(() =>
    loadPinnedIds(defaultPinnedIds)
  );

  const setShowPinnedOnly = useCallback((value: boolean) => {
    setShowPinnedOnlyState(value);
    savePinnedPref(value);
  }, []);

  const handlePinnedToggle = useCallback(
    (pinned: boolean) => {
      setShowPinnedOnly(pinned);
    },
    [setShowPinnedOnly]
  );

  const handleTogglePin = useCallback((chartId: string) => {
    setPinnedIds((prev) => {
      const next = prev.includes(chartId)
        ? prev.filter((id) => id !== chartId)
        : [...prev, chartId];
      savePinnedIds(next);
      return next;
    });
  }, []);

  return {
    showPinnedOnly,
    setShowPinnedOnly,
    pinnedIds,
    setPinnedIds,
    handlePinnedToggle,
    handleTogglePin,
  };
}
